// Client-side help search — an Orama full-text index built in-browser from the
// already-precached topic corpus (src/help/topics/*.md).
//
// Why runtime, not a deploy-time index file (plan §7 sketched the latter):
// at this corpus size the topics are already in the SW precache, so indexing
// them in-browser adds ZERO new precache bytes — it sidesteps the plan's
// precache-budget checkpoint entirely. Orama itself is dynamically imported
// (like marked) so it stays out of the eager bundle and loads only when the
// palette first opens. If the corpus ever grows past a few hundred topics,
// swap this for a deploy-time prebuilt index (Orama's `persist`), which is why
// the search API is kept behind this one module.
//
// Typo tolerance (D3's reason for Orama over Lunr) matters for multilingual
// low-literacy typists on an English-only UI.

import { loadAllHelpSearchDocs } from '@/lib/helpTopics'

export interface HelpSearchResult {
  id:       string
  title:    string
  priority: string
  /** A short plain-text snippet of the body for the result row. */
  snippet:  string
}

// orama's types come from the dynamic import; keep this module dep-light.
type OramaDb = unknown
let dbPromise: Promise<OramaDb> | null = null

async function getDb(): Promise<OramaDb> {
  if (!dbPromise) dbPromise = build()
  return dbPromise
}

async function build(): Promise<OramaDb> {
  const [{ create, insertMultiple }, docs] = await Promise.all([
    import('@orama/orama'),
    loadAllHelpSearchDocs(),
  ])
  const db = await create({
    schema: { id: 'string', title: 'string', body: 'string', priority: 'string' },
  })
  await insertMultiple(db, docs.map(d => ({
    id: d.id, title: d.title, body: d.body, priority: d.priority,
  })))
  return db
}

/** Warm the index ahead of first keystroke (e.g. when the palette opens). */
export function warmHelpSearch(): void {
  void getDb()
}

export async function searchHelp(query: string, limit = 6): Promise<HelpSearchResult[]> {
  const q = query.trim()
  if (q.length < 2) return []
  const [{ search }, db] = await Promise.all([import('@orama/orama'), getDb()])
  const res = await (search as (db: unknown, params: unknown) => Promise<{
    hits: { document: { id: string; title: string; body: string; priority: string } }[]
  }>)(db, {
    term: q,
    properties: ['title', 'body'],
    boost: { title: 2 },
    tolerance: 1,       // typo tolerance
    limit,
  })
  return res.hits.map(h => ({
    id:       h.document.id,
    title:    h.document.title,
    priority: h.document.priority,
    snippet:  snippetFor(h.document.body, q),
  }))
}

// A short plain-text lead-in around the first query hit (or the opening line),
// with markdown syntax stripped for a clean result row.
function snippetFor(body: string, query: string): string {
  const plain = body
    .replace(/^#+\s+/gm, '')
    .replace(/[*_`>#-]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  const at = plain.toLowerCase().indexOf(query.toLowerCase())
  const start = at > 40 ? at - 30 : 0
  const slice = plain.slice(start, start + 100).trim()
  return (start > 0 ? '…' : '') + slice + (plain.length > start + 100 ? '…' : '')
}
